import React, { useState, useEffect, useCallback } from 'react';
import universalDataService from '../services/universalDataService';

// ── WMO Weather Codes → label + emoji ────────────────────────────────────────
const WMO = {
  0:  { label: 'Senin',               icon: '☀️' },
  1:  { label: 'Predominant senin',   icon: '🌤️' },
  2:  { label: 'Parțial noros',       icon: '⛅' },
  3:  { label: 'Înnorat',             icon: '☁️' },
  45: { label: 'Ceață',               icon: '🌫️' },
  48: { label: 'Ceață cu chiciură',   icon: '🌫️' },
  51: { label: 'Burniță ușoară',      icon: '🌦️' },
  53: { label: 'Burniță moderată',    icon: '🌦️' },
  55: { label: 'Burniță densă',       icon: '🌧️' },
  61: { label: 'Ploaie ușoară',       icon: '🌧️' },
  63: { label: 'Ploaie moderată',     icon: '🌧️' },
  65: { label: 'Ploaie abundentă',    icon: '🌧️' },
  71: { label: 'Ninsoare ușoară',     icon: '🌨️' },
  73: { label: 'Ninsoare moderată',   icon: '❄️' },
  75: { label: 'Ninsoare abundentă',  icon: '❄️' },
  77: { label: 'Grindină',            icon: '🌨️' },
  80: { label: 'Averse ușoare',       icon: '🌦️' },
  81: { label: 'Averse moderate',     icon: '🌧️' },
  82: { label: 'Averse puternice',    icon: '⛈️' },
  85: { label: 'Averse ninsoare',     icon: '🌨️' },
  86: { label: 'Averse ninsoare',     icon: '❄️' },
  95: { label: 'Furtună',             icon: '⛈️' },
  96: { label: 'Furtună cu grindină', icon: '⛈️' },
  99: { label: 'Furtună cu grindină', icon: '⛈️' },
};
const wmo = (code) => WMO[code] || { label: 'Necunoscut', icon: '🌡️' };

const DAYS_RO   = ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'];
const MONTHS_RO = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtDay = (dateStr) => {
  const d = new Date(dateStr);
  return `${DAYS_RO[d.getDay()]} ${d.getDate()} ${MONTHS_RO[d.getMonth()]}`;
};


// ── Open-Meteo fetch — 16 days ────────────────────────────────────────────────
const fetchWeather = async (lat, lng) => {
  const url =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lng}` +
    `&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum,windspeed_10m_max,precipitation_probability_max` +
    `&current_weather=true` +
    `&timezone=Europe%2FBucharest` +
    `&forecast_days=16`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

// ── Component ─────────────────────────────────────────────────────────────────
const Weather = () => {
  const [communes, setCommunes]     = useState([]); // [{ name, lat, lng, totalHa }]
  const [weatherMap, setWeatherMap] = useState({}); // { comuneName: weatherData }
  const [loading, setLoading]       = useState(true);
  const [errors, setErrors]         = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);

  // ── Derive communes from fields (also track total ha per commune) ─────────
  const buildCommunes = useCallback(async () => {
    const fields = await universalDataService.loadFields().catch(() => null)
      || (() => {
        const farmId = localStorage.getItem('currentFarmId');
        if (!farmId) return [];
        try {
          const raw = localStorage.getItem(`fieldsData_${farmId}`);
          if (!raw) return [];
          const p = JSON.parse(raw);
          return p.data || p || [];
        } catch { return []; }
      })();

    if (!Array.isArray(fields) || !fields.length) return [];

    const map = {};
    fields.forEach(f => {
      if (!f.coordinates?.length) return;
      const name = (f.comuna || '').trim() || 'Necunoscută';
      if (!map[name]) map[name] = { lats: [], lngs: [] };
      f.coordinates.forEach(c => { map[name].lats.push(c.lat); map[name].lngs.push(c.lng); });
    });

    return Object.entries(map).map(([name, { lats, lngs }]) => ({
      name,
      lat: (lats.reduce((a, b) => a + b) / lats.length).toFixed(5),
      lng: (lngs.reduce((a, b) => a + b) / lngs.length).toFixed(5),
    }));
  }, []);

  // ── Fetch weather for all communes ───────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    setErrors({});
    const comms = await buildCommunes();
    setCommunes(comms);

    if (!comms.length) { setLoading(false); return; }

    const newMap = {};
    const newErrors = {};
    await Promise.all(
      comms.map(async c => {
        try {
          newMap[c.name] = await fetchWeather(c.lat, c.lng);
        } catch (e) {
          newErrors[c.name] = e.message;
        }
      })
    );
    setWeatherMap(newMap);
    setErrors(newErrors);
    setLastUpdated(new Date());
    setLoading(false);
  }, [buildCommunes]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Agri advice from weather (14-day window) ──────────────────────────────
  const getAdvice = (daily) => {
    const advice = [];
    const rain7d  = (daily.precipitation_sum || []).slice(0, 7).reduce((a, b) => a + b, 0);
    const rain14d = (daily.precipitation_sum || []).slice(0, 14).reduce((a, b) => a + b, 0);
    const maxWind = Math.max(...(daily.windspeed_10m_max || []).slice(0, 3));
    const minTemp = Math.min(...(daily.temperature_2m_min || []).slice(0, 3));

    if (rain7d > 40) advice.push({ type: 'warn', text: 'Precipitații semnificative în 7 zile — evitați lucrările cu utilaje grele' });
    else if (rain7d > 10) advice.push({ type: 'info', text: 'Precipitații ușoare în 7 zile — condiții bune după uscare' });
    if (rain14d === 0) advice.push({ type: 'info', text: 'Fără precipitații prognozate în 14 zile — verificați umiditatea solului' });
    if (maxWind > 40) advice.push({ type: 'warn', text: 'Vânt puternic — amânați tratamentele chimice' });
    if (minTemp < 2 && minTemp > -5) advice.push({ type: 'warn', text: 'Risc de îngheț ușor — monitorizați culturile sensibile' });
    if (minTemp <= -5) advice.push({ type: 'danger', text: 'Temperaturi sub -5°C — risc major pentru culturi' });
    if (rain7d === 0 && (daily.temperature_2m_max?.[0] || 0) > 28) advice.push({ type: 'info', text: 'Timp uscat și cald — condiții optime pentru erbicidare și recoltare' });
    if (!advice.length) advice.push({ type: 'ok', text: 'Condiții meteorologice favorabile pentru lucrările câmpului' });
    return advice;
  };

  const adviceColor = { warn: '#f59e0b', danger: '#ef4444', info: '#3b82f6', ok: '#22c55e' };
  const adviceBg   = { warn: '#fef3c7', danger: '#fee2e2', info: '#dbeafe', ok: '#dcfce7' };

  // ── Render ────────────────────────────────────────────────────────────────
  const S = styles;

  if (loading) return (
    <div style={S.page}>
      <div style={S.header}>
        <h2 style={S.title}>☀️ Vreme</h2>
        <span style={S.sub}>Se încarcă datele meteo…</span>
      </div>
      <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🌤️</div>
        <div>Detectăm comunele și aducem prognoza 14 zile…</div>
      </div>
    </div>
  );

  if (!communes.length) return (
    <div style={S.page}>
      <div style={S.header}>
        <h2 style={S.title}>☀️ Vreme</h2>
      </div>
      <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🗺️</div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Nicio comună detectată</div>
        <div>Adăugați câmpuri cu coordonate în secțiunea <strong>Câmpuri</strong> — vremea va apărea automat per comună.</div>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>☀️ Vreme — Prognoză 14 Zile</h2>
          <span style={S.sub}>
            {communes.length} {communes.length === 1 ? 'comună' : 'comune'} detectate
            {lastUpdated && ` · Actualizat ${lastUpdated.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}`}
          </span>
        </div>
        <button onClick={refresh} style={S.refreshBtn} title="Actualizează prognoza">
          🔄 Actualizează
        </button>
      </div>

      {/* One card per commune */}
      {communes.map(com => {
        const w   = weatherMap[com.name];
        const err = errors[com.name];

        if (err) return (
          <div key={com.name} style={S.card}>
            <div style={S.cardTitle}>{com.name}</div>
            <div style={{ color: '#ef4444', padding: '12px 0' }}>⚠️ Eroare la obținerea datelor: {err}</div>
          </div>
        );
        if (!w) return null;

        const cur   = w.current_weather;
        const daily = w.daily;

        // ── 14-day rain totals ──────────────────────────────────────────────
        const days14      = (daily.time || []).slice(0, 14);
        const precip14    = (daily.precipitation_sum || []).slice(0, 14);
        const totalRainMm = precip14.reduce((a, b) => a + (b || 0), 0);
        const rainDays    = precip14.filter(p => p > 0).length;

        const advice = getAdvice(daily);

        return (
          <div key={com.name} style={S.card}>
            {/* Card header — current weather */}
            <div style={S.cardHeader}>
              <div>
                <div style={S.cardTitle}>{com.name}</div>
                <div style={S.coords}>{com.lat}°N, {com.lng}°E</div>
              </div>
              <div style={S.currentWeather}>
                <span style={{ fontSize: 56 }}>{wmo(cur.weathercode).icon}</span>
                <div style={{ marginLeft: 16 }}>
                  <div style={S.currentTemp}>{Math.round(cur.temperature)}°C</div>
                  <div style={S.currentDesc}>{wmo(cur.weathercode).label}</div>
                  <div style={S.currentWind}>💨 {Math.round(cur.windspeed)} km/h</div>
                </div>
              </div>
            </div>

            {/* ── 14-day rain summary banner ──────────────────────────────── */}
            <div style={S.rainSummary}>
              <div style={S.rainSummaryLeft}>
                <span style={{ fontSize: 32 }}>{totalRainMm > 0 ? '🌧️' : '☀️'}</span>
                <div style={{ marginLeft: 12 }}>
                  <div style={{ fontWeight: 800, fontSize: 26, color: totalRainMm > 0 ? '#1d4ed8' : '#d97706', lineHeight: 1 }}>
                    {totalRainMm.toFixed(1)} mm
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>= {totalRainMm.toFixed(1)} L/m²</div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                    precipitații prognozate în 14 zile ·{' '}
                    {rainDays === 0
                      ? 'nicio zi cu ploaie'
                      : `${rainDays} ${rainDays === 1 ? 'zi cu ploaie' : 'zile cu ploaie'}`}
                  </div>
                </div>
              </div>
              {totalRainMm === 0 && (
                <div style={{ color: '#d97706', fontWeight: 600, fontSize: 13 }}>
                  Fără precipitații prognozate în 14 zile
                </div>
              )}
            </div>

            {/* Agri advice */}
            <div style={{ marginBottom: 16 }}>
              {advice.map((a, i) => (
                <div key={i} style={{ ...S.adviceBanner, background: adviceBg[a.type], borderLeftColor: adviceColor[a.type] }}>
                  {a.type === 'ok' ? '✅' : a.type === 'warn' ? '⚠️' : a.type === 'danger' ? '🚨' : 'ℹ️'} {a.text}
                </div>
              ))}
            </div>

            {/* 14-day forecast — 2 rows of 7 */}
            <div style={S.forecastGrid}>
              {days14.map((day, i) => {
                const prec = daily.precipitation_sum?.[i] || 0;
                const prob = daily.precipitation_probability_max?.[i] || 0;
                const isToday = i === 0;
                const isWeek2 = i >= 7;
                return (
                  <div key={day} style={{
                    ...S.dayCard,
                    background: isToday ? '#f0fdf4' : isWeek2 ? '#f8f9ff' : '#fafafa',
                    borderColor: isToday ? '#22c55e' : prec > 0 ? '#bfdbfe' : '#e5e7eb',
                    opacity: isWeek2 ? 0.88 : 1,
                  }}>
                    <div style={{ ...S.dayName, color: isWeek2 ? '#9ca3af' : '#374151' }}>
                      {isToday ? 'Azi' : fmtDay(day)}
                    </div>
                    <div style={{ fontSize: 24, margin: '4px 0' }}>{wmo(daily.weathercode?.[i]).icon}</div>
                    <div style={S.dayTemp}>
                      <span style={{ color: '#ef4444', fontWeight: 700 }}>{Math.round(daily.temperature_2m_max?.[i])}°</span>
                      <span style={{ color: '#3b82f6', marginLeft: 3 }}>{Math.round(daily.temperature_2m_min?.[i])}°</span>
                    </div>
                    {prec > 0
                      ? <div style={S.dayRain}>💧 {prec.toFixed(1)}<span style={{ fontSize: 9 }}>mm</span></div>
                      : <div style={{ fontSize: 10, color: '#d1d5db' }}>—</div>
                    }
                    {prob > 0 && (
                      <div style={{ fontSize: 10, color: prob >= 60 ? '#1d4ed8' : '#6b7280', fontWeight: prob >= 60 ? 700 : 400 }}>{prob}%</div>
                    )}
                    <div style={S.dayWind}>💨 {Math.round(daily.windspeed_10m_max?.[i])}</div>
                  </div>
                );
              })}
            </div>
            {/* Week 2 label */}
            <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 4 }}>
              Rândul 2 = zilele 8–14 (precizie mai redusă la distanță mare)
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  page:     { padding: '24px', maxWidth: 1100, margin: '0 auto' },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title:    { margin: 0, fontSize: 24, fontWeight: 700, color: '#1f2937' },
  sub:      { color: '#6b7280', fontSize: 13 },
  refreshBtn: { padding: '8px 16px', background: '#2d6a4f', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  card:     { background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  cardTitle:  { fontSize: 20, fontWeight: 700, color: '#1f2937' },
  coords:     { fontSize: 12, color: '#9ca3af', marginTop: 2 },
  currentWeather: { display: 'flex', alignItems: 'center' },
  currentTemp:    { fontSize: 36, fontWeight: 800, color: '#1f2937', lineHeight: 1 },
  currentDesc:    { fontSize: 15, color: '#374151', marginTop: 2 },
  currentWind:    { fontSize: 13, color: '#6b7280', marginTop: 4 },
  // Rain summary banner
  rainSummary: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
    border: '1px solid #bfdbfe', borderRadius: 10,
    padding: '14px 18px', marginBottom: 16, flexWrap: 'wrap', gap: 12,
  },
  rainSummaryLeft:  { display: 'flex', alignItems: 'center' },
  adviceBanner:   { padding: '8px 12px', borderRadius: 6, borderLeft: '4px solid', marginBottom: 6, fontSize: 13, fontWeight: 500 },
  // 14-day grid: 7 per row, wraps to 2 rows naturally
  forecastGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 },
  dayCard:  { borderRadius: 8, padding: '8px 4px', textAlign: 'center', border: '1px solid #e5e7eb', transition: 'opacity .2s' },
  dayName:  { fontSize: 10, fontWeight: 700, textTransform: 'uppercase' },
  dayTemp:  { fontSize: 13, marginBottom: 2 },
  dayRain:  { fontSize: 12, color: '#1d4ed8', fontWeight: 600 },
  dayWind:  { fontSize: 10, color: '#6b7280', marginTop: 2 },
};

export default Weather;
